import { QueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

/**
 * The app's single TanStack Query client.
 *
 * Module-level (not created in a component) so that non-React callers — React Router loaders in
 * Phase B — can import this exact instance and prime it with `ensureQueryData`, and the page's
 * `useQuery` then reads the primed entry with no second fetch.
 *
 * Defaults:
 *  - `staleTime: 30s` — revisiting a page within 30s renders instantly from cache with no refetch;
 *    after that the cached data still shows immediately while a background revalidation runs.
 *  - no retry on a 4xx, except 408 (timeout) and 429 (rate limited) — the rest are the server's
 *    answer, not a transient failure, and every attempt goes through the shared client's error
 *    interceptor, so a retried 404 opened the same error dialog three times (role audit,
 *    2026-10-11). Network errors and 5xx still retry. See `shouldRetry`.
 *  - `refetchOnWindowFocus: false` — avoids surprise refetches when the clinician tabs back in.
 */
/** Retry a failed query at most twice — but only when trying again could change the answer. */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  const status = (error as AxiosError)?.response?.status;
  if (status !== undefined && status >= 400 && status < 500 && status !== 408 && status !== 429) {
    return false;
  }
  return failureCount < 2;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: shouldRetry,
    },
  },
});
